import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { Empty } from 'components/Empty';
import { TooltipIcon, TooltipWrapper } from 'components/Tooltip';
import { User } from 'components/User';
import { IPayout } from 'types';
import { capitalize, centsToDollarsDisplay, centsToNegativeDollarsDisplay, DEFAULT_PAGE_SIZE, formatDateForUI, getTimeWithZone, isSortDesc, isSortedBy } from 'utils';
import { Cell } from '../Cell';
import HeaderCell from '../HeaderCell';
import HeaderRow from '../HeaderRow';
import Row from '../Row';
import SortLabel from '../SortLabel';
import Table from '../Table';
import TableBody from '../TableBody';
import TableHead from '../TableHead';

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

interface IPaidPaymentsRowProps {
  payout?: IPayout;
  isLoading?: boolean;
  onClick?: () => void;
}

const PaidPaymentsRow = ({ payout, isLoading, onClick }: IPaidPaymentsRowProps) => {
  const submissionDate = (payout?.statusHistory || []).find((s) => s.action === 'create')?.createdAt;
  const submissionDateFormatted = submissionDate && formatDateForUI(submissionDate);
  const submissionTime = submissionDate && getTimeWithZone(submissionDate);

  return (
    <Row clickable onClick={onClick} data-cy={isLoading ? 'loading-row' : 'paid-payments-row'} data-value={payout?.id}>
      <Cell data-cy="payee-name">
        <User hasPaymentMethod={false} id={payout?.userId} userId={payout?.userId} isLoading={isLoading} username={payout?.employee.name} />
      </Cell>

      <Cell minWidth={110} data-cy="payout-id">
        {isLoading ? <Skeleton width={188} /> : payout?.id}
      </Cell>

      <Cell minWidth={160} data-cy="business-day">
        {isLoading ? (
          <Skeleton width={160} />
        ) : (
          <TooltipWrapper data-cy="tooltip-wrapper">
            {`${payout?.paymentDate} `}
            <TooltipIcon id={`tooltip-${payout?.id}`}>
              <p>
                <strong>Date Submitted</strong>
                <br />
                {submissionDateFormatted}
                <br />
                {submissionTime}
              </p>
            </TooltipIcon>
          </TooltipWrapper>
        )}
      </Cell>

      <Cell align="right" minWidth={88} data-cy="gross">
        {isLoading ? <Skeleton width={88} /> : centsToDollarsDisplay(payout?.gross)}
      </Cell>

      <Cell align="right" minWidth={88} data-cy="holdback">
        {isLoading ? <Skeleton width={88} /> : centsToNegativeDollarsDisplay(payout?.holdback)}
      </Cell>

      <Cell align="right" minWidth={88} data-cy="net">
        {isLoading ? <Skeleton width={88} /> : <strong>{centsToDollarsDisplay(payout?.net)}</strong>}
      </Cell>

      <Cell minWidth={88} data-cy="disbursement">
        {isLoading ? <Skeleton width={88} /> : capitalize(payout?.disbursementMethod)}
      </Cell>
    </Row>
  );
};

interface IPaidPaymentsTableProps {
  isLoading: boolean;
  payouts: IPayout[];
  urlQuery: IApiQueryOptions;
  onSortChange: (sort: string) => void;
}

const PaidPaymentsTable: React.FC<IPaidPaymentsTableProps> = ({ isLoading, payouts, urlQuery, onSortChange }) => {
  const currentSort = urlQuery.sort || '';
  const isDescendingSort = isSortDesc(currentSort);
  const [nameSorted, paymentDateSorted, grossSorted, holdbackSorted, netSorted, disbursementSorted] = useMemo(
    () => ['employee_name', 'payment_date', 'gross', 'holdback', 'net', 'disbursement_method'].map((header) => isSortedBy(header, currentSort)),
    [currentSort]
  );

  return (
    <Layout data-cy="reports-paid-payments-table">
      <Table sticky scrollable>
        <TableHead>
          <HeaderRow sticky>
            <HeaderCell>
              <SortLabel data-cy="header-employee-name" label="Name" isSorted={nameSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('employee_name')} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell>{!isLoading && 'Payment ID'}</HeaderCell>

            <HeaderCell>
              <SortLabel data-cy="header-payment-date" label="Business Day" isSorted={paymentDateSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('payment_date')} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell align="right">
              <SortLabel data-cy="gross-header" label="Gross" isSorted={grossSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('gross')} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell align="right">
              <SortLabel data-cy="holdback-header" label="Holdback" isSorted={holdbackSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('holdback')} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell align="right">
              <SortLabel data-cy="net-header" label="Net" isSorted={netSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('net')} isLoading={isLoading} />
            </HeaderCell>

            <HeaderCell>
              <SortLabel data-cy="disbursement-header" label="Disbursement" isSorted={disbursementSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('disbursement_method')} isLoading={isLoading} />
            </HeaderCell>
          </HeaderRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            [...Array(DEFAULT_PAGE_SIZE)].map((_, i) => <PaidPaymentsRow key={i} isLoading />)
          ) : payouts?.length === 0 ? (
            <Row>
              <Cell colSpan={8}>
                <Empty container containerHeight={300} icon="Payment" message="No results. Try a different date range." />
              </Cell>
            </Row>
          ) : (
            payouts?.map((payout: IPayout) => <PaidPaymentsRow key={payout.id} payout={payout} isLoading={isLoading} />)
          )}
        </TableBody>
      </Table>
    </Layout>
  );
};

export default PaidPaymentsTable;
